'use client';

import { useState, useEffect, useRef } from 'react';
import { Sparkles, Loader2, X, ChevronDown, Check, Search, Brain, PenLine, CheckCircle2, Eye } from 'lucide-react';
import { getLeadGroups, type LeadGroup } from '@/lib/lead-groups';
import { streamAICall, type AIStatusEvent } from '@/lib/sse';
import { useEscapeKey } from '@/hooks/useEscapeKey';
import { useToast } from '@/hooks/useToast';

interface AIGenerateModalProps {
  open: boolean;
  onClose: () => void;
  onComplete: (data: { leads: any[]; count: number; leadGroupId: string }) => void;
}

type ExclusionMode = 'none' | 'all' | 'specific';

export function AIGenerateModal({ open, onClose, onComplete }: AIGenerateModalProps) {
  useEscapeKey(onClose, open);

  const toast = useToast();
  const [prompt, setPrompt] = useState('');
  const [count, setCount] = useState(10);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [statusEvents, setStatusEvents] = useState<AIStatusEvent[]>([]);
  const [thinkingText, setThinkingText] = useState('');
  const [thinkingExpanded, setThinkingExpanded] = useState(false);
  const logRef = useRef<HTMLDivElement>(null);

  // Exclusion state
  const [groups, setGroups] = useState<LeadGroup[]>([]);
  const [exclusionMode, setExclusionMode] = useState<ExclusionMode>('all');
  const [excludedGroupIds, setExcludedGroupIds] = useState<Set<string>>(new Set());
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Load lead groups when modal opens
  useEffect(() => {
    if (open) {
      getLeadGroups().then(setGroups).catch(() => toast.error('Failed to load lead lists'));
    }
  }, [open]);

  function toggleGroupExclusion(id: string) {
    setExcludedGroupIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function handleGenerate() {
    if (!prompt.trim()) {
      setError('Describe your target audience to generate leads');
      return;
    }
    setGenerating(true);
    setError('');
    setStatusEvents([]);
    setThinkingText('');
    setThinkingExpanded(false);

    // Determine which group IDs to exclude
    let excludeGroupIds: string[] | undefined;
    if (exclusionMode === 'all') {
      excludeGroupIds = groups.map((g) => g.id);
    } else if (exclusionMode === 'specific' && excludedGroupIds.size > 0) {
      excludeGroupIds = Array.from(excludedGroupIds);
    }

    const API_URL = (import.meta.env.VITE_API_URL || '') + '/api';
    streamAICall<{ leads: any[]; count: number; leadGroupId: string }>({
      url: `${API_URL}/ai/generate-leads`,
      body: { industry: prompt, count, excludeGroupIds },
      onStatus: (event) => {
        if (event.type === 'thinking_delta') {
          // Append thinking text deltas (streaming Claude's reasoning)
          setThinkingText((prev) => prev + event.message);
          return;
        }
        setStatusEvents((prev) => {
          // Replace if same type (except searching which accumulates)
          if (event.type !== 'searching' && event.type !== 'analyzing') {
            return [...prev.filter((e) => e.type !== event.type), event];
          }
          return [...prev, event];
        });
        // Auto-scroll the log
        setTimeout(() => logRef.current?.scrollTo({ top: logRef.current.scrollHeight, behavior: 'smooth' }), 50);
      },
      onResult: (data) => {
        setGenerating(false);
        onComplete(data);
        setPrompt('');
        setCount(10);
        setStatusEvents([]);
        setThinkingText('');
        setExclusionMode('none');
        setExcludedGroupIds(new Set());
      },
      onError: (msg) => {
        setGenerating(false);
        setError(msg);
        setStatusEvents([]);
        setThinkingText('');
      },
    });
  }

  if (!open) return null;

  const exclusionLabel =
    exclusionMode === 'none' ? 'None'
    : exclusionMode === 'all' ? 'All existing leads'
    : excludedGroupIds.size === 0 ? 'Select lists...'
    : `${excludedGroupIds.size} list${excludedGroupIds.size !== 1 ? 's' : ''} excluded`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-base-100/95" onClick={generating ? undefined : onClose} />
      <div className="relative bg-base-100 border border-base-300/30 rounded-2xl p-6 w-full max-w-lg mx-4 space-y-5 animate-fade-in shadow-2xl">
        {!generating && (
          <button onClick={onClose} className="absolute top-4 right-4 btn btn-ghost btn-xs btn-circle">
            <X size={14} />
          </button>
        )}

        <div>
          <h2 className="text-lg font-bold">Find Leads with AI</h2>
          <p className="text-sm text-base-content/40 mt-1">
            Describe your ideal prospects and we&apos;ll generate matching leads.
          </p>
        </div>

        {!generating ? (
          <>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-base-content/40 mb-1 block">Describe your target audience *</label>
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder={"e.g. CTOs at SaaS companies in the DACH region with 50-200 employees, focused on fintech or proptech"}
                  className="textarea textarea-bordered w-full text-sm leading-relaxed min-h-[120px] resize-none"
                />
              </div>

              {/* Bottom row: count + exclusion side by side */}
              <div className="flex gap-3">
                <div className="w-32 shrink-0">
                  <label className="text-xs text-base-content/40 mb-1 block">Number of leads</label>
                  <input type="number" value={count}
                    onChange={(e) => setCount(Math.max(1, Number(e.target.value) || 1))}
                    min={1} className="input input-bordered input-sm w-full" />
                </div>

                <div className="flex-1 relative">
                  <label className="text-xs text-base-content/40 mb-1 block">Exclude existing leads</label>
                  <button
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                    className="w-full input input-bordered input-sm flex items-center justify-between text-left text-sm"
                  >
                    <span className={exclusionMode === 'none' ? 'text-base-content/40' : ''}>{exclusionLabel}</span>
                    <ChevronDown size={12} className={`text-base-content/30 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {dropdownOpen && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-base-100 border border-base-300/30 rounded-xl shadow-2xl z-50 animate-fade-in overflow-hidden max-h-64 overflow-y-auto">
                      {/* None option */}
                      <button
                        onClick={() => { setExclusionMode('none'); setExcludedGroupIds(new Set()); setDropdownOpen(false); }}
                        className={`w-full px-4 py-2.5 text-left text-sm flex items-center gap-2 hover:bg-base-200/50 ${exclusionMode === 'none' ? 'text-primary font-medium' : ''}`}
                      >
                        {exclusionMode === 'none' && <Check size={12} className="text-primary" />}
                        <span className={exclusionMode !== 'none' ? 'ml-5' : ''}>None</span>
                      </button>

                      {/* All leads option */}
                      <button
                        onClick={() => { setExclusionMode('all'); setExcludedGroupIds(new Set()); setDropdownOpen(false); }}
                        className={`w-full px-4 py-2.5 text-left text-sm flex items-center gap-2 hover:bg-base-200/50 ${exclusionMode === 'all' ? 'text-primary font-medium' : ''}`}
                      >
                        {exclusionMode === 'all' && <Check size={12} className="text-primary" />}
                        <span className={exclusionMode !== 'all' ? 'ml-5' : ''}>All existing leads</span>
                        <span className="text-[10px] text-base-content/30 ml-auto">{groups.reduce((s, g) => s + g.leadCount, 0)} leads</span>
                      </button>

                      {groups.length > 0 && (
                        <>
                          <div className="border-t border-base-content/5 mx-2 my-1" />
                          <p className="px-4 py-1 text-[10px] font-semibold text-base-content/30 uppercase tracking-wider">Select specific lists</p>
                        </>
                      )}

                      {groups.map((g) => {
                        const isChecked = exclusionMode === 'specific' && excludedGroupIds.has(g.id);
                        return (
                          <button
                            key={g.id}
                            onClick={() => {
                              setExclusionMode('specific');
                              toggleGroupExclusion(g.id);
                            }}
                            className="w-full px-4 py-2 text-left text-sm flex items-center gap-2 hover:bg-base-200/50"
                          >
                            <input
                              type="checkbox"
                              className="checkbox checkbox-xs checkbox-primary"
                              checked={isChecked}
                              readOnly
                            />
                            <span className="truncate flex-1">{g.name}</span>
                            <span className="text-[10px] text-base-content/30 shrink-0">{g.leadCount}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-2">
                <p className="text-xs text-error flex-1">{error}</p>
                <button onClick={handleGenerate} className="btn btn-ghost btn-xs text-primary gap-1">Try again</button>
              </div>
            )}

            <div className="flex items-center justify-end gap-2">
              <button onClick={onClose} className="btn btn-ghost btn-sm">Cancel</button>
              <button onClick={handleGenerate} disabled={!prompt.trim()}
                className="btn btn-primary btn-sm gap-1">
                <Sparkles size={14} /> Generate
              </button>
            </div>
          </>
        ) : (
          <div className="space-y-4 py-4">
            <div className="flex flex-col items-center gap-3 text-center">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Loader2 size={22} className="text-primary animate-spin" />
              </div>
              <div>
                <p className="text-sm font-medium">Generating {count} leads...</p>
                <p className="text-xs text-base-content/40 mt-1 max-w-xs truncate">
                  {prompt}
                </p>
              </div>
            </div>

            {/* Real-time AI status feed */}
            <div ref={logRef} className="bg-base-200/30 rounded-xl p-3 max-h-56 overflow-y-auto space-y-1.5">
              {statusEvents.length === 0 && !thinkingText && (
                <div className="flex items-center gap-2 text-xs text-base-content/40">
                  <Loader2 size={12} className="animate-spin" />
                  <span>Connecting to AI...</span>
                </div>
              )}

              {/* Collapsible thinking stream */}
              {thinkingText && (
                <div className="mb-2">
                  <button
                    onClick={() => setThinkingExpanded((v) => !v)}
                    className="flex items-center gap-1.5 text-[11px] text-base-content/40 hover:text-base-content/60 transition-colors"
                  >
                    <Brain size={11} />
                    <span>Claude is thinking...</span>
                    <Eye size={10} className={thinkingExpanded ? 'text-primary' : ''} />
                  </button>
                  {thinkingExpanded && (
                    <pre className="mt-1.5 text-[11px] text-base-content/30 leading-relaxed whitespace-pre-wrap break-words max-h-24 overflow-y-auto font-mono bg-base-200/40 rounded-lg px-2.5 py-2">
                      {thinkingText}
                    </pre>
                  )}
                </div>
              )}

              {/* Discrete status events (searches, analysis, writing) */}
              {statusEvents.map((event, i) => {
                const isLatest = i === statusEvents.length - 1;
                const icon = event.type === 'thinking' ? <Brain size={12} />
                  : event.type === 'searching' ? <Search size={12} />
                  : event.type === 'analyzing' ? <Loader2 size={12} className="animate-spin" />
                  : event.type === 'writing' ? <PenLine size={12} />
                  : event.type === 'done' ? <CheckCircle2 size={12} className="text-success" />
                  : null;
                return (
                  <div
                    key={i}
                    className={`flex items-start gap-2 text-xs transition-opacity ${
                      isLatest ? 'text-primary font-medium' : 'text-base-content/30'
                    } ${event.type === 'searching' ? 'text-info/80 font-medium' : ''}`}
                  >
                    {isLatest && event.type !== 'done' ? (
                      <Loader2 size={12} className="animate-spin shrink-0 mt-0.5" />
                    ) : (
                      <span className="shrink-0 mt-0.5">{icon}</span>
                    )}
                    <span className="break-words">{event.message}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
